import { Button, Input, Label } from '@betstarters/ui'
import { useTranslation } from 'react-i18next'
import { ErrorBanner } from './catalog'
import { Checkbox, Combobox } from './controls'
import type { ReferenceCountry } from '../hooks/useReference'

/**
 * The create/edit forms of the Geo area, shared by the list dialogs and the
 * detail pages — one definition of the fields, their limits and their hints.
 */

// ---- country ---------------------------------------------------------------

export interface CountryFormValue {
  country: string
  englishShortName: string
  isoCode2: string
  isoCode3: string
  isCompulsoryDocument: boolean
  numericCode: string
}

export const emptyCountryForm: CountryFormValue = {
  country: '',
  englishShortName: '',
  isoCode2: '',
  isoCode3: '',
  isCompulsoryDocument: false,
  numericCode: '',
}

export function isCountryFormValid(v: CountryFormValue): boolean {
  return v.country.trim() !== '' && v.isoCode2.trim() !== ''
}

export function CountryForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  pending,
  error,
  submitLabel,
}: {
  value: CountryFormValue
  onChange: (v: CountryFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<CountryFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isCountryFormValid(value)) onSubmit()
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="country-name">{t('countries.columns.name')}</Label>
        <Input
          id="country-name"
          value={value.country}
          onChange={(e) => set({ country: e.target.value })}
          maxLength={50}
          placeholder="ITALY"
          autoFocus
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="country-english">{t('countries.columns.englishName')}</Label>
        <Input
          id="country-english"
          value={value.englishShortName}
          onChange={(e) => set({ englishShortName: e.target.value })}
          maxLength={500}
          placeholder="Italy"
        />
      </div>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="country-iso2">{t('countries.columns.iso2')}</Label>
          <Input
            id="country-iso2"
            value={value.isoCode2}
            onChange={(e) => set({ isoCode2: e.target.value })}
            maxLength={50}
            placeholder="IT"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="country-iso3">{t('countries.columns.iso3')}</Label>
          <Input
            id="country-iso3"
            value={value.isoCode3}
            onChange={(e) => set({ isoCode3: e.target.value })}
            maxLength={3}
            placeholder="ITA"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="country-numeric">{t('countries.columns.numericCode')}</Label>
          <Input
            id="country-numeric"
            value={value.numericCode}
            onChange={(e) => set({ numericCode: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })}
            placeholder="380"
          />
        </div>
      </div>

      <Checkbox
        id="country-compulsory-document"
        checked={value.isCompulsoryDocument}
        onCheckedChange={(checked) => set({ isCompulsoryDocument: checked })}
        label={t('countries.columns.compulsoryDocument')}
      />

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isCountryFormValid(value)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

// ---- jurisdiction ----------------------------------------------------------

export interface JurisdictionFormValue {
  jurisdiction: string
}

export const emptyJurisdictionForm: JurisdictionFormValue = { jurisdiction: '' }

export function isJurisdictionFormValid(v: JurisdictionFormValue): boolean {
  return v.jurisdiction.trim() !== ''
}

export function JurisdictionForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  pending,
  error,
  submitLabel,
}: {
  value: JurisdictionFormValue
  onChange: (v: JurisdictionFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isJurisdictionFormValid(value)) onSubmit()
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="jurisdiction-name">{t('jurisdictions.columns.name')}</Label>
        <Input
          id="jurisdiction-name"
          value={value.jurisdiction}
          onChange={(e) => onChange({ jurisdiction: e.target.value })}
          maxLength={40}
          placeholder="Curacao"
          autoFocus
        />
      </div>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isJurisdictionFormValid(value)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

// ---- prefix ----------------------------------------------------------------

export interface PrefixFormValue {
  prefixId: string
  prefix: string
  countryId: string
  countryName: string
  isoCode2: string
}

export const emptyPrefixForm: PrefixFormValue = {
  prefixId: '',
  prefix: '',
  countryId: '',
  countryName: '',
  isoCode2: '',
}

export function isPrefixFormValid(v: PrefixFormValue, isCreate: boolean): boolean {
  return (
    v.prefix.trim() !== '' &&
    v.countryName.trim() !== '' &&
    v.isoCode2.trim() !== '' &&
    (!isCreate || v.prefixId !== '')
  )
}

export function PrefixForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  countries,
  isCreate,
  pending,
  error,
  submitLabel,
}: {
  value: PrefixFormValue
  onChange: (v: PrefixFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  countries: ReferenceCountry[]
  /** On create the caller picks the id; on edit it is immutable. */
  isCreate: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<PrefixFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isPrefixFormValid(value, isCreate)) onSubmit()
      }}
    >
      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="prefix-id">{t('prefixes.columns.id')}</Label>
          <Input
            id="prefix-id"
            value={value.prefixId}
            onChange={(e) => set({ prefixId: e.target.value.replace(/[^0-9]/g, '') })}
            disabled={!isCreate}
            placeholder="700"
            autoFocus={isCreate}
          />
          <p className="text-xs text-muted-foreground">{t('prefixes.idHint')}</p>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="prefix-value">{t('prefixes.columns.prefix')}</Label>
          <Input
            id="prefix-value"
            value={value.prefix}
            onChange={(e) => set({ prefix: e.target.value })}
            maxLength={20}
            placeholder="39"
            autoFocus={!isCreate}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="prefix-country">{t('prefixes.columns.linkedCountry')}</Label>
        <Combobox
          value={value.countryId}
          onValueChange={(v) => set({ countryId: v })}
          items={countries.map((c) => ({ value: String(c.id), label: `${c.isoCode2} — ${c.name}` }))}
          placeholder={t('prefixes.noCountry')}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">{t('prefixes.countryHint')}</p>
      </div>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="prefix-label">{t('prefixes.columns.countryName')}</Label>
          <Input
            id="prefix-label"
            value={value.countryName}
            onChange={(e) => set({ countryName: e.target.value })}
            maxLength={50}
            placeholder="Italy"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="prefix-iso">{t('prefixes.columns.iso2')}</Label>
          <Input
            id="prefix-iso"
            value={value.isoCode2}
            onChange={(e) => set({ isoCode2: e.target.value })}
            maxLength={20}
            placeholder="IT"
          />
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">{t('prefixes.labelHint')}</p>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isPrefixFormValid(value, isCreate)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
